import { i18nReady } from './i18n';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { bootFontSize } from '@tpt/design';
import App from './App';
import { paintTheme, readSavedTheme, normalizeTheme } from './api/theme';
import { installPrefsSync } from './api/prefsSync';
import { installSafetyValidator } from './api/safetyValidator';
import { installTptAuthFetch } from './api/tptAuthFetch';
import { installErrorReporter } from './api/errorReport';
import { installVersionGuard } from './api/versionGuard';
import { installServerProxy } from './desktop/serverProxy';
import { isTauri } from './desktop';
import { isPopoutWindow } from './desktop/env';
import { pushInternalPath } from './utils/openExternal';
import { applyPlatformAttr } from './utils/platform';
import { RC_ORIGIN, downloadViaBlob, saveGeneratedFile, installFileImgHealer } from './api/rcClient';
import { resolveDownloadName } from './api/fileName';
import { installDiag, diagLog } from './desktop/diag';
import { bootUiScale, bootScaleSync, bootZoom } from './api/uiScale';
import { syncImageSendQuality } from './api/prefs';
import './styles/tokens.css';
import './index.css';

installDiag();              // 진단 로거 — 전역 에러 캡처 (데스크톱 콘솔 안 보임 대비)
diagLog('app boot', 'href=', typeof window !== 'undefined' ? window.location.href : 'n/a');

// 🔄 stale 청크 자동 복구 — 새 배포 후 브라우저가 캐시된 옛 index.html 의 청크 해시를 요청해
//   "Failed to fetch dynamically imported module" 404 가 나면, 캐시 무시하고 1회 강제 새로고침해 최신 index 를 받는다.
//   (무한 새로고침 방지: sessionStorage 플래그로 10초 내 1회만)
if (typeof window !== 'undefined') {
	const RELOAD_KEY = 'tpt-chunk-reload-ts';
	const recover = (reason: string) => {
		try {
			const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
			if (Date.now() - last < 10_000) return; // 직전에 이미 새로고침 — 루프 차단
			sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
		} catch { /* private mode — 그래도 1회 시도 */ }
		diagLog('chunk reload', reason);
		// 캐시 무시 reload — index.html 재요청 → 최신 청크 해시 확보
		window.location.reload();
	};
	const isChunkErr = (msg: string) => /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|ChunkLoadError/i.test(msg);
	window.addEventListener('vite:preloadError', (e) => { e.preventDefault(); recover('vite:preloadError'); });
	window.addEventListener('unhandledrejection', (e) => { const m = String((e as PromiseRejectionEvent).reason?.message || (e as PromiseRejectionEvent).reason || ''); if (isChunkErr(m)) recover('unhandledrejection'); });
	window.addEventListener('error', (e) => { if (isChunkErr(String((e as ErrorEvent).message || ''))) recover('error-event'); });
}
// 📱 알림 탭 딥링크 조기 캡처 (2026-08-23 사용자 "알림 클릭이 방으로 안 갈 때가 있음") — APK 콜드스타트는
//   webview onPageFinished 시점에 'tpt:open-room' 을 주입하는데, React(ChatPage 리스너)가 아직 안 떠
//   있으면 이벤트가 통째로 유실됐다(웜스타트만 동작 = 간헐 증상). 모듈 로드 즉시 리스너로 보관만 하고,
//   useRealtimeNotify 가 마운트/채널 로드 시 소비한다(3분 TTL). ChatPage 핸들러가 처리하면 스탬프를 지운다.
//   🖥 데탑 보강 (2026-08-27 사용자 "데탑에서 알림을 클릭하면 해당 방으로 안 들어가는 경우가 종종") — 알림 클릭으로 메인 창이
//   전면 복귀하는 순간 versionGuard(visibilitychange)가 새 버전을 보면 새로고침한다 → 메모리 예약이 통째로 유실(배포 잦은 날 재현).
//   sessionStorage 에도 함께 보관해 새로고침을 넘긴다(같은 창 안에서만 살고, useRealtimeNotify 가 소비·삭제, 3분 TTL).
if (typeof window !== 'undefined') {
	window.addEventListener('tpt:open-room', (e) => {
		const rid = (e as CustomEvent<{ roomId?: string }>).detail?.roomId;
		if (!rid) return;
		const stamp = { rid, at: Date.now() };
		(window as unknown as { __tptPendingOpenRoom?: { rid: string; at: number } }).__tptPendingOpenRoom = stamp;
		try { sessionStorage.setItem('tpt-pending-open-room', JSON.stringify(stamp)); } catch { /* private mode */ }
		// 🧭 채팅 화면 밖(/projects·/feeds 등 — 앱·좁은 화면은 프로젝트가 같은 창)에서 오면 받을 ChatPage 가 없어 예약만 남고
		//   사용자가 3분 안에 채팅으로 돌아와야 열렸다 (2026-09-29 형 "pjt가 켜있으면 바로가기 누르면 이동 안 됨") → 채팅으로 보내 마운트 시 소비.
		//   별도 창·로그인 계열은 건드리지 않는다. 한 박자 뒤 — 같은 틱에 nav('/chat') 을 먼저 부르는 호출부(DrivePage)와 겹치지 않게.
		window.setTimeout(() => {
			const p = window.location.pathname;
			if (/^\/(chat|room)(\/|$)/.test(p) || /^\/(login|signup|invite|onboarding|download)(\/|$)/.test(p) || isPopoutWindow()) return;
			pushInternalPath('/chat');
		}, 0);
	});
}

installServerProxy();       // 데스크톱(Tauri): 상대 서버경로(/api,/avatar,…) → 절대 서버 URL. 웹은 무동작.
installSafetyValidator();   // 영구 수정 #34 — destructive RC API 차단
installTptAuthFetch();      // 사이드카(/api/tpt/*) 호출에 인증 헤더 자동 첨부 (2026-08-18 권한 감사 — 서버 가드 부착 선행조건)
installErrorReporter();     // 🩺 오류 표본 수집 (2026-08-22) — 간헐 버그 진단용, 개인정보·수집량 캡은 api/errorReport.ts
installVersionGuard();      // 🔄 화면 버전 가드 (2026-08-22) — 전면 복귀 시 서버 최신 웹버전과 다르면 새로고침 (앱 웹뷰 옛 화면 잔존 해소)

// 데스크톱(Tauri) — webview 기본 우클릭 메뉴(공유/뒤로/새로고침/소스보기 등) 차단.
// 단 입력창·편집영역·선택된 텍스트에선 허용(복사·붙여넣기 UX 보존). 웹 브라우저는 무동작.
if (typeof document !== 'undefined' && isTauri()) {
	// 🩺 창 위치·크기 복원 진단 (2026-10-27 형 원격 "오시영님 매번 사이즈 초기화") — 셸이 남긴 저장·복원 결과를 읽어
	//   파일에서 정상 복원(restore: file)이 아니면 서버 오류 표본으로 보낸다(어느 PC 에서 왜 실패하는지 확인용). 팝아웃 창은 제외.
	if (!isPopoutWindow()) {
		window.setTimeout(() => {
			void import('@tauri-apps/api/core').then(({ invoke }) => invoke<string>('winstate_diag')).then((d) => {
				if (d && !/^restore: file$/.test(d)) void import('./api/errorReport').then(({ reportDiag }) => reportDiag('winstate', d));
			}).catch(() => { /* 구버전 셸 — 커맨드 없음 */ });
		}, 8000);
	}
	document.addEventListener('contextmenu', (e) => {
		const t = e.target as HTMLElement | null;
		if (t && t.closest('input, textarea, [contenteditable="true"]')) return;
		if (window.getSelection()?.toString()) return;
		e.preventDefault();
	});
	// 🌐 외부 http(s) 링크 → OS 기본 브라우저 (2026-07-10) — webview 는 target=_blank 새 창 요청을
	//   차단해 대화 속 URL 클릭이 아무 반응 없던 것. a[download](파일 저장)와 서버 자원(RC_ORIGIN
	//   파일/아바타 프록시)은 기존 동작 유지. 앵커 자체 onClick 이 preventDefault 했으면 존중.
	document.addEventListener('click', (e) => {
		if (e.defaultPrevented) return;
		const a = (e.target as HTMLElement | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
		if (!a) return;
		const href = a.getAttribute('href') || '';
		// 📥 <a download> 안전망 (2026-08-07 #2) — Tauri 웹뷰는 cross-origin <a download> 를 무시해
		//   완전 무반응(미리보기 중앙 다운로드 버튼 등). onClick 이 preventDefault 안 한 download 앵커는
		//   여기서 가로채 downloadViaBlob(→ Rust save_download)으로 저장. blob: 은 fetch→saveGeneratedFile.
		if (a.hasAttribute('download')) {
			if (!/^(https?:\/\/|blob:|\/)/i.test(href)) return;
			e.preventDefault();
			void (async () => {
				// 📎 확장자 확정 (2026-08-13) — 전달된 파일 링크(`?share=`)는 URL 에 파일명이 없어
				//   예전 로직이 "file"(확장자 없음)로 저장했다. resolveDownloadName 이 name·URL 을
				//   함께 보고, 확장자를 못 찾으면 downloadViaBlob 이 서버 헤더까지 확인한다.
				const name = resolveDownloadName(a.getAttribute('download') || '', href);
				if (/^blob:/i.test(href)) await saveGeneratedFile(await (await fetch(href)).blob(), name || 'download');
				else await downloadViaBlob(href, name || 'download');
			})().catch((err) => diagLog('anchor download fail', String(err)));
			return;
		}
		if (!/^https?:\/\//i.test(href)) return;
		if (RC_ORIGIN && href.startsWith(RC_ORIGIN)) return;
		e.preventDefault();
		void (async () => {
			const { invoke } = await import('@tauri-apps/api/core');
			await invoke('open_external', { url: href });
		})().catch((err) => diagLog('open_external fail', String(err)));
	});
}

// 🆕 테마 마이그레이션 (2026-06-10) — 2테마(light/dark) → 3 레일 테마(navy/blue/red).
// 레거시 값(light/dark/friendly/business/minimal)은 navy(기본) 로 정규화해 저장.
// 다크 테마 제거 — '시스템 테마 따르기'(tpt-theme-system) 도 함께 폐기.
try {
	const cur = localStorage.getItem('tpt-theme');
	// 'black' = 울트라 블랙 (2026-07-09 신설, 유일한 다크 계열)
	// 🔴 화이트리스트를 여기 따로 두지 않는다 — 2026-09-20 navyrail 이 여기서 'navy' 로 눌려 프로젝트/링크 진입(전체 리로드)마다 테마가 되돌아갔다
	const norm = normalizeTheme(cur);
	if (cur !== norm) localStorage.setItem('tpt-theme', norm);
	localStorage.removeItem('tpt-theme-system');
} catch { /* private mode */ }

applyPlatformAttr();  // 🖥 html[data-platform] — 플랫폼별 글자 톤 보정(index.css)이 이 속성을 본다
installFileImgHealer();   // 🩹 만료·미부착 파일 핸들로 실패한 <img> 를 새 핸들로 다시 받는다 (2026-09-27, api/rcClient)
paintTheme(readSavedTheme(), 'boot');
installPrefsSync();   // ⚙️ 개인 설정 기기 간 동기화 — 로그인 감지·창 복귀 pull, 팝아웃 테마 storage 동기 (2026-09-07)
bootFontSize();
bootUiScale();
bootZoom();        // 🔎 미세 배율(--tpt-zoom) — 사용자가 슬라이더로 고른 100% 기준 배율
bootScaleSync();   // 창 간 배율 동기화 — AI/캘린더/방 팝아웃이 부모의 글자 크기 변경을 실시간 반영
// 📉 사진 전송 화질 — 관리자가 정한 전역값을 받아 캐시에 반영 (2026-08-13 개인 설정 → 관리자 이관).
//    실패해도 기존 캐시(기본 50%)로 계속 동작하므로 await 하지 않는다.
void syncImageSendQuality();

// StrictMode 미사용 — Yjs/Hocuspocus collaboration provider 가 dev double-invoke 시
// mount→unmount→mount 로 ws 연결이 깨지는 알려진 문제. (production 은 StrictMode 무관)
// 🚚 렌더는 i18n 로케일 청크 로드 후 (2026-08-30 lazy 분리) — 첫 페인트 번역 키 노출 방지.
//   로드 실패(오프라인 등)여도 렌더는 한다 — fallback ko 미로드 시 키가 보이는 게 빈 화면보다 낫다.
void i18nReady.catch(() => undefined).then(() => {
	ReactDOM.createRoot(document.getElementById('root')!).render(
		<BrowserRouter>
			<App />
		</BrowserRouter>,
	);
});
